// Tracks which grades the student has already seen, so the dashboard can flag
// newly arrived (or re-graded) results. Client-only: localStorage keyed per
// person. Same pattern as the portal's other UI-state keys.

type SeenMap = Record<string, number>; // submissionId -> finalPoints last seen

function key(personId: string | null | undefined): string {
  return `portal-v2:gradesSeen:${personId ?? 'anon'}`;
}

function read(personId: string | null | undefined): SeenMap {
  try {
    const raw = localStorage.getItem(key(personId));
    return raw ? (JSON.parse(raw) as SeenMap) : {};
  } catch {
    return {};
  }
}

function write(personId: string | null | undefined, map: SeenMap): void {
  try {
    localStorage.setItem(key(personId), JSON.stringify(map));
  } catch {
    /* quota — ignore */
  }
}

/** A grade is "new" if never seen, or its points changed since last seen (re-grade). */
export function isGradeNew(
  personId: string | null | undefined,
  submissionId: string,
  finalPoints: number | undefined,
): boolean {
  if (finalPoints === undefined) return false;
  const seen = read(personId);
  return !(submissionId in seen) || seen[submissionId] !== finalPoints;
}

/** Mark a grade as seen (call when the student opens the graded view). */
export function markGradeSeen(
  personId: string | null | undefined,
  submissionId: string,
  finalPoints: number | undefined,
): void {
  if (finalPoints === undefined) return;
  const seen = read(personId);
  if (seen[submissionId] === finalPoints) return;
  seen[submissionId] = finalPoints;
  write(personId, seen);
}
